import Link from 'next/link'
import { TextBase } from '@components/v2/text-base'
import { MarketComparison } from '../../market-comparison'

import styles from './styles.module.scss'

const freeMarketData = {
  heading: 'Mercado livre (ACL)',
  content: [
    'Consumidores podem escolher o(s) fornecedor(es) da sua energia de qualquer lugar do país.',
    'Os preços e condições de contratação da energia podem ser negociados livremente entre os comercializadores/geradores e os consumidores finais.',
    'Consumidores recebem duas ou mais faturas, uma da concessionária local pelo serviço de distribuição da energia e a(s) outra(s) do(s) gerador(es) ou comercializador(es) pelo custo da energia.'
  ]
}

const captiveMarketData = {
  heading: 'Mercado cativo (ACR)',
  content: [
    'Consumidores não têm opção, exceto comprar a energia da distribuidora da sua região.',
    'Preços das tarifas de energia e condições de negociação são regulados pela Aneel.',
    'Consumidores recebem apenas uma fatura (conta de luz) mensal, que inclui os custos da energia e distribuição.'
  ]
}

const freeMarketAdvantages = {
  heading: 'Vantagens',
  content: [
    'Liberdade de escolha do fornecedor.',
    'Economia de até 29% na conta de luz.',
    'Previsibilidade nos gastos com energia.',
    'Sustentabilidade com uso de energias renováveis.',
    'Traz vantagem competitividade para os consumidores.'
  ]
}

const freeMarketDisadvantages = {
  heading: 'Desvantagens',
  content: [
    'Requer conhecimento do setor.',
    'Acompanhamento mensal da gestão do contrato.',
    'Requer acompanhamento de portifólio de contratação, não existe renovação automática do contrato.',
    'Cumprimento de determinações CCEE.'
  ]
}

export const questions = [
  {
    title: 'O que é o mercado livre de energia?',
    content: (
      <div className={styles.content}>
        <TextBase>
          <strong>O mercado livre de energia</strong>, ou{' '}
          <strong>Ambiente de Contratação Live (ACL)</strong>, é o ambiente do
          setor elétrico brasileiro regulado pela{' '}
          <strong>Câmara de Comercialização de Energia Elétrica (CCEE)</strong>{' '}
          em que consumidores podem negociar contratos bilaterais para a compra
          e venda de sua energia elétrica com fornecedores de qualquer parte do
          país.
        </TextBase>
        <br />
        <TextBase>
          Criado pela resolução{' '}
          <strong>
            Nº 265 da Agência Nacional de Energia Elétrica (ANEEL)
          </strong>
          , de 13 de agosto de 1998, o mercado livre responde atualmente por 35%
          de toda a energia elétrica consumida no Brasil e engloba um total de
          mais de 12 mil agentes, divididos entre comercializadores,
          distribuidores e <strong>consumidores livres e especiais.</strong>
        </TextBase>
      </div>
    )
  },
  {
    title: 'Como funciona o mercado livre de energia elétrica?',
    content: (
      <div className={styles.content}>
        <TextBase>
          O mercado livre funciona pela contratação voluntária de energia pelos
          consumidores, que podem comprar sua energia elétrica de empresas
          geradoras ou comercializadoras como alternativa ao suprimento da
          concessionária local e com condições comerciais livremente negociadas,
          incluindo preço, período de suprimento, quantidade de energia,
          condições de pagamento, índices de reajustes etc.
        </TextBase>
        <br />
        <TextBase>
          Ou seja, é o ambiente oposto ao mercado de energia tradicional,
          conhecido como mercado cativo ou{' '}
          <strong>Ambiente de Contratação Regulada (ACR)</strong>, no qual os
          consumidores podem consumir energia apenas da distribuidora de sua
          região e obrigatoriamente aos preços e condições fixados pela{' '}
          <strong>ANEEL</strong> para todos os consumidores do mercado regulado.
        </TextBase>
        <br />
        <TextBase>
          <strong>No ambiente de contratação livre</strong>, os consumidores
          podem escolher geradores e/ou comercializadores de energia elétrica de
          qualquer parte do país e integrantes do{' '}
          <strong>Sistema Interligado Nacional (SIN)</strong>, com os quais
          firmam contratos bilaterais de curto, médio ou longo prazo com
          condições livremente negociadas para o fornecimento da energia.
        </TextBase>
        <br />
        <TextBase>
          Assim como no <strong>mercado regulado</strong>, a energia produzida
          no mercado livre é enviada até os consumidores por meio das redes de
          transmissão e malhas elétricas das transmissoras e distribuidoras de
          cada região. Dessa forma, o livre mercado de energia terá seu preço
          final composto pelo custo de geração e de distribuição, além dos
          encargos e impostos.
        </TextBase>
        <br />
        <TextBase>
          Então, ao entrar para o <strong>mercado livre de energia</strong>, o
          consumidor passa a realizar dois pagamentos diferentes, um para o(s)
          fornecedor(es), referente ao custo da energia contratada, e outro para
          a concessionária local, referente ao serviço de distribuição dessa
          energia.
        </TextBase>
        <br />
        <TextBase>
          As operações de venda e{' '}
          <Link
            href="/compra-de-energia-eletrica-no-mercado-livre"
            passHref
            target="_blank"
          >
            compra de energia no mercado livre
          </Link>{' '}
          devem ser registradas na CCEE, entidade privada e sem fins lucrativos,
          responsável por viabilizar a comercialização de energia elétrica no
          Brasil.
        </TextBase>
      </div>
    )
  },
  {
    title: 'Quem pode migrar para o mercado livre de energia?',
    content: (
      <div className={styles.content}>
        <TextBase>
          Hoje, apenas grandes consumidores atendidos em média ou alta tensão
          podem migrar para o <strong>mercado livre</strong> de energia
          elétrica, com uma demanda contratada mínima de 1000 kW para que se
          tornem <strong>consumidores livres</strong> ou de 500 kW para
          ingressarem como <strong>consumidores especiais.</strong>
        </TextBase>
        <br />
        <TextBase>
          De maneira mais simples, isso significa que praticamente qualquer
          consumidor com uma conta de luz maior que R$ 35.000,00 pode migrar
          para o mercado livre de energia.
        </TextBase>
        <br />
        <TextBase>
          Ou seja, os consumidores residenciais, assim como pequenos comércios,
          ainda não têm{' '}
          <Link
            href="/como-migrar-para-o-mercado-livre-de-energia"
            passHref
            target="_blank"
          >
            como migrar para o mercado livre de energia
          </Link>
          , ficando limitados ao mercado de energia tradicional, o Ambiente de
          Contratação Regulado (o mercado cativo), e à compra de energia
          exclusivamente da distribuidora que atende a sua região.
        </TextBase>
        <br />
        <TextBase>
          Dessa forma, ainda não existe um mercado livre de energia residencial,
          mas já existem discussões regulatórias para promover a abertura total
          para todos os consumidores nos próximos anos.
        </TextBase>
        <br />
        <TextBase>
          Consumidores que não atendam a demanda contratada mínima exigida para
          migração ao mercado de energia livre (<strong>ACL</strong>) podem se
          unir através da chamada comunhão de cargas, sendo ela de dois tipos:
        </TextBase>
        <br />
        <TextBase>
          <strong>Comunhão de direito:</strong> unidades consumidoras
          pertencentes ao mesmo CNPJ e localizadas no mesmo submercado de
          energia do país (Sudeste/Centro-Oeste, Sul, Norte e Nordeste).
        </TextBase>
        <br />
        <TextBase>
          <strong>Comunhão de fato:</strong> unidades consumidoras de diferentes
          CNPJ e localizadas em áreas contíguas, isto é, que sejam vizinhas e
          não estejam separadas por ruas ou qualquer outro tipo de logradouro.
        </TextBase>
      </div>
    )
  },
  {
    title: 'Por que o mercado livre de energia é mais barato?',
    content: (
      <div className={styles.content}>
        <TextBase>
          O preço da energia elétrica é mais barato no mercado livre em relação
          ao mercado de energia tradicional porque a concorrência entre as
          geradoras e comercializadoras exige que elas pratiquem valores mais
          baixos para atrair os consumidores, que dessa forma, conseguem obter
          uma redução de cerca de 29% na conta de luz ao migrar para o{' '}
          <strong>ACL.</strong>
        </TextBase>
        <br />
        <TextBase>
          Além disso, os consumidores do <strong>mercado livre</strong> são
          isentos da cobrança de bandeiras tarifárias e podem obter descontos
          que variam de 50%, 80% ou 100% na{' '}
          <strong>Tarifa de Uso do Sistema de Distribuição (TUSD)</strong> ao
          consumirem energia incentivada proveniente de fontes renováveis.
        </TextBase>
        <br />
        <TextBase>
          <strong>O mercado livre de energia</strong>, além de mais barato,
          permite um maior controle e mais segurança no seu custo com
          eletricidade, uma vez que os contratos de compra e venda de energia
          podem ser negociados no longo prazo, com preços e índices de reajustes
          definidos para todo o período.
        </TextBase>
      </div>
    )
  },
  {
    title: 'Como funciona a venda e compra de energia no mercado livre?',
    content: (
      <div className={styles.content}>
        <TextBase>
          <strong>No mercado livre</strong>, a contratação de energia é feita
          diretamente entre os consumidores e produtores, ou por intermédio de
          comercializadores, que firmam contratos bilaterais de curto, médio ou
          longo prazo, enquanto a entrega física da energia continua sob a
          responsabilidade da distribuidora regional.
        </TextBase>
        <br />
        <TextBase>
          Existem dois tipos de energia negociadas no{' '}
          <strong>mercado livre:</strong> a convencional, oriunda de usinas
          tradicionais do setor elétrico (grandes hidrelétricas, termelétricas
          fósseis e nuclear); e a incentivada, proveniente de usinas movidas por
          fontes alternativas (solar fotovoltaica, eólica, biomassa e pequenas
          centrais hidrelétricas), com potência até 30.000 kW (30 MW).
        </TextBase>
        <br />
        <TextBase>
          <strong>
            Consumidores livres podem comprar energia no mercado de energia
            livre (ACL) de qualquer tipo, incentivada ou convencional, enquanto
            consumidores especiais só podem contratar energia incentivada.
          </strong>
        </TextBase>
      </div>
    )
  },
  {
    title: 'O que é PLD no mercado livre de energia?',
    content: (
      <div className={styles.content}>
        <TextBase>
          O Preço de Liquidação das Diferenças <strong>(PLD)</strong> é um valor
          - R$/MWh - definido diariamente pela <strong>CCEE</strong> para cada
          submercado de energia do país e utilizado como referência para os
          acertos de possíveis sub ou sobrecontratação de energia pelos
          consumidores do mercado livre, realizados no{' '}
          <strong>Mercado de Curto Prazo (MCP).</strong>
        </TextBase>
        <br />
        <TextBase>
          Isto é, caso um consumidor do <strong>mercado livre</strong>, em
          determinado mês, tenha consumido mais ou menos energia que o volume
          contratado, essa diferença, se não for negociada diretamente com outro
          agente do <strong>ACL</strong> no prazo estipulado, lhe deverá ser
          debitada ou creditada pela <strong>CCEE</strong> ao valor do{' '}
          <strong>PLD</strong> correspondente.
        </TextBase>
        <br />
        <TextBase>
          Os valores do <strong>PLD</strong> são definidos diariamente pela{' '}
          <strong>CCEE</strong> para cada submercado (Norte, Nordeste,
          Sudeste-Centro Oeste e Sul) e para cada hora do dia seguinte, sendo
          que os preços são limitados por um valor mínimo e máximo, os quais são
          determinados pela <strong>ANEEL</strong> a cada ano.
        </TextBase>
        <br />
        <TextBase>
          Para definição do <strong>PLD, a CCEE</strong> utiliza modelos
          matemáticos (Newave, Decomp e Dessem) que têm por objetivo encontrar
          um equilíbrio entre capacidade de fornecimento, demanda atual e
          armazenamento futuro, além de ter como base o{' '}
          <strong>Custo Marginal de Operação (CMO)</strong> que é publicado
          semanalmente pelo <strong>Operador Nacional do Sistema (ONS).</strong>
        </TextBase>
        <br />
        <TextBase>
          <strong>
            Vários fatores são considerados no cálculo do PLD e influenciam no
            seu valor, incluindo:
          </strong>
        </TextBase>
        <br />
        <ul>
          <li>produção das usinas hidrelétricas;</li>
          <li>condições e previsões pluviométricas;</li>
          <li>demanda de consumo de energia;</li>
          <li>preços dos combustíveis;</li>
          <li>custo de déficit;</li>
          <li>
            previsão e entrada de operação de novos empreendimentos de geração e
            transmissão;
          </li>
        </ul>
        <br />
        <TextBase>
          No entanto, é importante lembrar que o <strong>PLD</strong> e sua
          oscilação de valor não impactam no preço da energia já contratada no
          longo prazo, a qual é negociada a preços não associados ao{' '}
          <strong>PLD</strong> e estipulada no contrato durante todo o seu
          período de validade.
        </TextBase>
      </div>
    )
  },
  {
    title:
      'O que é energia elétrica incentivada e convencional no mercado livre?',
    content: (
      <div className={styles.content}>
        <TextBase>
          No mercado livre, a energia produzida pelos geradores e contratada
          pelos consumidores pode ser de dois tipos, convencional ou
          incentivada, sendo que apenas a segunda garante desconto na{' '}
          <strong>Tarifa do Uso do Sistema de Distribuição (TUSD).</strong>
        </TextBase>
        <br />
        <TextBase>
          A energia convencional é proveniente das usinas elétricas tradicionais
          e mais utilizadas no setor elétrico brasileiro, isto é, são as grandes
          hidrelétricas ou as termelétricas a gás natural, carvão, óleo diesel,
          óleo combustível ou nuclear.
        </TextBase>
        <br />
        <TextBase>
          Existem duas subclassificações: energia convencional não especial, que
          pode ser contratada apenas por <strong>consumidores livres</strong>; e
          a energia convencional não especial, que pode ser contratada por
          consumidores livres e consumidores especiais. Nenhuma delas garante
          desconto na <strong>TUSD.</strong>
        </TextBase>
        <br />
        <TextBase>
          Já a energia incentivada é proveniente de usinas elétricas menores,
          com até 30.000 kW de potência instalada, e movidas por fontes
          renováveis que promovem a redução dos impactos ambientais do setor
          elétrico, como a energia solar fotovoltaica, eólica, biomassa, e
          Pequenas Centrais Hidrelétricas (PCH’s).
        </TextBase>
        <br />
        <TextBase>
          Para tornar a energia desses empreendimentos mais competitiva e
          estimular o seu uso pelos agentes do <strong>mercado livre</strong>, o
          Governo concede a ela diferentes níveis de desconto na{' '}
          <strong>TUSD</strong> , que podem ser de 50%, 80% ou 100%, além de
          estabelecer na regulação que consumidores de classificação especial
          (aqueles com demanda contratada entre 500kW e 999kW) devem
          obrigatoriamente adquirir energia incentivada.
        </TextBase>
        <br />
        <TextBase>
          Para fins de comercialização da energia, existe uma subdivisão dos
          tipos de energia:
        </TextBase>
        <br />

        <ul>
          <li>energia Incentivada Especial.</li>
          <li>energia Incentivada Não Especial.</li>
          <li>energia Convencional Não Especial.</li>
          <li>energia Convencional Especial.</li>
        </ul>
        <br />
        <TextBase>
          Os descontos concedidos dependem da classificação da usina.
        </TextBase>
        <br />
        <TextBase>
          <strong>
            100% - Empreendimentos que atendem os seguintes critérios:
          </strong>
        </TextBase>
        <br />
        <ul>
          <li>
            eólica, biomassa e cogeração: potência injetada até 30MW que
            iniciaram suas operações entre abril 2023 e dezembro de 2023.
          </li>
          <li>
            PCH: Pequenas Centrais Hidrelétricas com potência instalada entre 5
            MW e 30 MW que iniciaram suas operações entre dezembro/1999 e
            dezembro/2023.
          </li>
          <li>
            térmica biomassa de resíduos sólidos urbanos, biogás de aterro
            sanitários, biodigestores (resíduo animal ou vegetal) e lodo de
            estação de tratamento de esgoto.
          </li>
        </ul>
        <br />

        <TextBase>
          <strong>
            80% - Empreendimentos que atendem os seguintes critérios:
          </strong>
        </TextBase>
        <br />
        <ul>
          <li>
            solar: empreendimentos com potência injetada de até 50MW, com
            outorga/leilão após 2016 (o desconto concedido ocorre apenas nos 10
            primeiros anos, após este período o desconto é reduzido para 50%).
          </li>
        </ul>
        <br />
        <TextBase>
          <strong>
            50% - Empreendimentos que atendem os seguintes critérios:
          </strong>
        </TextBase>
        <br />
        <ul>
          <li>
            solar: empreendimentos com potência injetada até 30MW outorga/leilão
            após 2016.
          </li>
          <li>
            eólica, biomassa e cogeração: empreendimentos com potência injetada
            de até 30MW.
          </li>
          <li>
            PCH: Pequenas Centrais Hidrelétricas com potência instalada entre
            5MW e 30MW.
          </li>
          <li>
            CGH: Centrais Geradoras Hidrelétricas com potência igual ou inferior
            a 5MW.
          </li>
        </ul>
        <br />

        <TextBase>
          <strong>
            0% - Empreendimentos que atendem os seguintes critérios:
          </strong>
        </TextBase>
        <br />
        <ul>
          <li>PCH: potência instalada superior a 30MW.</li>
          <li>térmica e biomassa: potência instalada superior a 50MW.</li>
        </ul>
        <TextBase>
          Veja, abaixo, o número atual de empreendimentos (usinas) em operação e
          conectados ao SIN por tipo de fonte utilizada:
        </TextBase>
        <br />
        <div className={styles.imageWrapper}>
          <figure>
            <img
              src="/assets/sistema-interligado-nacional.svg"
              alt="Sistema interligado nacional – empreendimentos em operação"
            />
            <figcaption>
              Fonte: Sistema de Informações de Geração da Aneel (SIGA) _
              junho/2022
            </figcaption>
          </figure>
        </div>
        <br />
        <div className={styles.imageWrapper}>
          <img
            src="/assets/venda-de-energia-mercado-livre.svg"
            alt="Venda de energia no mercado livre – quantidade de usinas ligadas à rede nacional por fonte"
          />
        </div>
        <br />
        <div className={styles.imageWrapper}>
          <img
            src="/assets/energia-incentivada.svg"
            alt="energia incentivada - participação de projetos no Sistema Interligado Nacional."
          />
        </div>
        <br />
        <TextBase>
          É importante lembrar, contudo, que nem todos esses empreendimentos e a
          sua capacidade de geração atendem ao{' '}
          <strong>mercado livre de energia.</strong>
        </TextBase>
        <TextBase>
          De acordo com o levantamento feito pela{' '}
          <strong>
            Associação Brasileira dos Comercializadores de Energia (Abraceel)
          </strong>
          , atualmente,{' '}
          <strong>
            o ambiente de contratação livre consome 52% da energia produzida por
            empreendimentos de fontes renováveis e representa 35% do consumo
            nacional de energia elétrica do Brasil.
          </strong>
        </TextBase>
      </div>
    )
  },
  {
    title: 'O que é a Câmara de Comercialização de Energia Elétrica - CCEE?',
    content: (
      <div className={styles.content}>
        <TextBase>
          A{' '}
          <strong>Câmara de Comercialização de Energia Elétrica (CCEE)</strong>{' '}
          é um órgão pessoa jurídica de direito privado, sem fins lucrativos,
          criada pela Lei nº 10.848, de 15 de março de 2004 e Decreto nº 5.177,
          de 12 de agosto de 2004. Foi instituída pela Resolução Normativa{' '}
          <strong>ANEEL</strong> nº 109, de 26 de outubro de 2004, com a
          finalidade de viabilizar a comercialização de energia elétrica no
          Brasil.
        </TextBase>
        <br />
        <TextBase>
          A <strong>CCEE</strong> atua como uma entidade idônea que gerencia e
          fiscaliza as operações do mercado de energia, dentre as suas funções
          estão:
        </TextBase>
        <br />
        <ul>
          <li>
            contabilização dos montantes comercializados no mercado livre e
            regulado;
          </li>
          <li>
            liquidação financeira dos valores das operações no mercado de curto
            prazo;
          </li>
          <li>
            cálculo e divulgação do Preço de Liquidação das Diferenças{' '}
            <strong>(PLD);</strong>
          </li>
          <li>
            realizações dos Leilões de Energia da <strong>ANEEL;</strong>
          </li>
          <li>
            gestão da{' '}
            <strong>
              Conta da Energia de Reserva (CONER), Conta do Desenvolvimento
              Energético (CDE), e Conta do Programa de Incentivo às Fontes
              Alternativas de Energia Elétrica (PROINFA).
            </strong>
          </li>
        </ul>
        <TextBase>
          Todo consumidor do <strong>mercado livre</strong> deve ser um agente
          na <strong>CCEE</strong>. Em razão da complexidade de atuar nesse
          mercado, é recomendado que os consumidores procurem auxílio de
          empresas que oferecem serviços especializados com a finalidade de
          orientar e ajudar os consumidores a operar neste ambiente de
          contratação.
        </TextBase>
        <br />
        <TextBase>
          Veja, abaixo, o crescimento nos últimos anos do número de consumidores
          no ambiente livre de contratação:
        </TextBase>
        <br />
        <div className={styles.imageWrapper}>
          <figure>
            <img
              src="/assets/ambiente-de-contrataçao-livre.svg"
              alt="ambiente de contratação livre - número de consumidores no mercado livre"
            />
            <figcaption>
              Fonte:{' '}
              <Link
                href="https://www.ccee.org.br/relatoriodeadministracao/30-mercado-10-1.html"
                passHref
                target="_blank"
              >
                CCEE
              </Link>
            </figcaption>
          </figure>
        </div>
        <br />
        <TextBase>
          Ao final de 2021, o número de consumidores (livres e especiais)
          participantes do mercado livre chegou a 9.930, um aumento de 40,7%
          desde 2019, reflexo dos acontecimentos (pandemia e crise hídrica) que
          geraram impactos no setor elétrico e trouxeram pesados aumentos nos
          preços da energia no mercado regulado.
        </TextBase>
        <br />
        <TextBase>
          Pode parecer pequeno perto do universo de 89 milhões de consumidores
          do país (2021, Aneel), porém o número de consumidores livres vem
          crescendo exponencialmente no mercado de energia desde 2015. Só não é
          maior porque existem alguns critérios que precisam ser atendidos pelo
          proponente antes de conquistar a “liberdade” de escolha do fornecedor
          de energia.
        </TextBase>
      </div>
    )
  },
  {
    title: 'Qual a diferença entre mercado cativo e mercado livre de energia?',
    content: (
      <div className={styles.content}>
        <TextBase>
          Existem dois ambientes de contratação no mercado de energia no Brasil,
          o mercado cativo e o mercado livre, que, nos termos técnicos, são
          conhecidos respectivamente como Ambiente de Contratação Regulada (ACR)
          e Ambiente de Contratação Livre (ACL).
          <br />
        </TextBase>
        <TextBase className="text-center">
          Veja abaixo as diferenças entre o{' '}
          <Link
            href="/ambiente-de-contratacao-livre-e-regulada"
            passHref
            target="_blank"
          >
            ambiente de contratação livre e regulada
          </Link>
        </TextBase>
        <br />
        <div className={styles.imageWrapper}>
          <figure>
            <img
              src="/assets/diferenca-entre-mercado-livre-e-mercado-cativo.svg"
              alt="Mercado cativo e mercado livre de energia diferenças"
            />
            <figcaption>
              Fonte: Cartilha do Mercado Livre de Energia Elétrica da Associação
              Brasileira dos Comercializadores de Energia Elétrica (Abraceel),
              maio de 2019
            </figcaption>
          </figure>
        </div>
        <br />
        <TextBase>
          <strong>No mercado cativo ou mercado regulado</strong>, a contratação
          e distribuição da energia elétrica estão centralizadas nas
          concessionárias do serviço de distribuição de cada região, com os
          preços da energia (tarifas) e as condições de negociação reguladas
          pela{' '}
          <strong>
            Agência Nacional de Energia Elétrica (Aneel). Aqui os consumidores
            são chamados de cativos e não têm outra opção para a compra de
            energia.
          </strong>
        </TextBase>
        <br />
        <TextBase>
          <strong>O mercado livre de energia</strong>, por sua vez, é o ambiente
          no qual os consumidores podem comprar a sua energia elétrica de um ou
          mais geradores ou comercializadores, com os quais podem negociar
          livremente todas as condições comerciais que serão firmadas e regidas
          por meio de contratos bilaterais.{' '}
          <strong>
            Os consumidores que fazem parte do ACL são chamados de consumidores
            livres.
          </strong>
        </TextBase>
        <br />
        <TextBase>
          A livre negociação neste ambiente de contratação promove naturalmente
          a concorrência entre os fornecedores, trazendo competitividade e
          estimulando à inovação no mercado de energia em relação aos produtos e
          serviços adicionais ofertados, bem como a busca constante de redução
          dos preços.
        </TextBase>
        <br />
        <div className={styles.imageWrapper}>
          <MarketComparison
            title="Diferenças entre o mercado cativo e o mercado livre de energia"
            firstColData={freeMarketData}
            secondColData={captiveMarketData}
          />
        </div>
      </div>
    )
  },
  {
    title: 'O que é uma comercializadora do mercado livre de energia? ',
    content: (
      <div className={styles.content}>
        <TextBase>
          Uma comercializadora de energia é um agente do{' '}
          <strong>mercado livre habilitado na CCEE</strong> que atua, na maioria
          das vezes, como intermediador entre os geradores e consumidores de
          energia, negociando a compra e venda de por meio de contratos
          bilaterais com o objetivo de facilitar a contratação pelos
          consumidores. No entanto uma comercializadora pode vender e comprar
          energia de outras comercializadoras ou até mesmo de um consumidor.
          <br />
        </TextBase>
        <TextBase>
          <strong>
            Para ser um comercializador de energia é necessário autorização da
            ANEEL
          </strong>
          , de acordo com os termos da <strong>REN 678/2015</strong>, além de
          aderir à <strong>CCEE</strong>. Atualmente, o número de
          comercializadoras habilitadas no mercado de energia é de 464, sendo
          que 297 estão localizadas no estado de São Paulo.
        </TextBase>
        <TextBase>
          <br />
          Existem comercializadoras que atuam como gestoras de clientes no{' '}
          <strong>mercado livre de energia</strong>, com a finalidade de
          oferecer a venda e a gestão do contrato e facilitar a operação.
        </TextBase>
      </div>
    )
  },
  {
    title: 'O que é uma gestora de energia?',
    content: (
      <div className={styles.content}>
        <TextBase>
          Uma gestora de energia trabalha no mercado livre de forma a oferecer a
          redução de custos de energia para seus clientes, com estratégias de
          operação do portifólio de contratos e contratação de energia futura.
          <br />
          Algumas das funções de uma gestora são supervisionar e monitorar o
          consumo de energia, acompanhar mudanças de legislação do mercado de
          energia e oscilações de preço, propor soluções para minimizar custos
          gerenciáveis, defender os interesses dos clientes e zelar pelo
          cumprimento de cláusulas contratuais, além das obrigações na CCEE.
        </TextBase>
      </div>
    )
  },
  {
    title: 'Como escolher um comercializador? ',
    content: (
      <div className={styles.content}>
        <TextBase>
          Como qualquer processo de compra, em que existe a etapa de avaliação
          do vendedor, ao escolher um comercializador de energia, o consumidor
          estará escolhendo o responsável pelo fornecimento de energia de sua
          unidade de consumo,{' '}
          <strong> sendo assim, alguns pontos devem ser avaliados:</strong>
          <br />
        </TextBase>
        <ul>
          <li>
            certificar-se se a comercializadora possui autorização na{' '}
            <strong>ANEEL</strong> e se está devidamente habilitada na{' '}
            <strong>CCEE</strong>;
          </li>
          <li>
            avaliar se há processos em andamento contra o comercializador;
          </li>
          <li>considerar a saúde financeira da empresa.</li>
        </ul>
        <br />
        <TextBase>
          <strong>
            Além disso, é recomendado que se escolham comercializadoras que:
          </strong>
        </TextBase>
        <ul>
          <li>apresentem opções de energia renovável;</li>
          <li>
            invistam em tecnologia e sejam transparentes nos dados de consumo.
          </li>
        </ul>
        <br />
        <TextBase>
          Por fim, podemos concluir que a diferença entre uma comercializadora e
          uma gestora de energia é que a primeira atua de forma a intermediar a
          compra e venda de energia, enquanto a segunda realiza um trabalho
          especializado com foco em buscar melhorias e reduções de custos.
        </TextBase>
      </div>
    )
  },
  {
    title: 'Como migrar para o mercado livre de energia? Passo a Passo',
    content: (
      <div className={styles.content}>
        <TextBase>
          Veja abaixo o passo a passo da migração para o mercado livre de
          energia:
        </TextBase>
        <br />
        <ol className="list-decimal list-inside">
          <li>
            <strong>Avaliar os requisitos de tensão e demanda</strong>
          </li>
          <br />
          <TextBase>
            Para migrar ao mercado livre de energia é necessário pertencer ao
            grupo tarifário “A” composto por grandes consumidores e possuir uma
            demanda contratada de, no mínimo, 500 kW para se tornar consumidor
            especial e de 1000 kW para ser um consumidor livre. <br />
          </TextBase>
          <br />

          <li>
            <strong>Analisar os contratos vigentes com a distribuidora</strong>
          </li>

          <br />
          <TextBase>
            Será preciso rescindir com seis meses de antecedência o contrato
            vigente de compra de energia regulada (ou contrato de fornecimento)
            com a distribuidora, o qual costuma ter vigência de um ano.
          </TextBase>
          <br />

          <li>
            <strong>Realizar estudo de viabilidade econômica</strong>
          </li>
          <br />

          <TextBase>
            Um passo extremamente importante é o estudo de viabilidade econômica
            para migração ao mercado livre, quando se compara as previsões de
            gastos com eletricidade no mercado livre e no cativo, a fim de
            apurar a economia entre os ambientes de contratação.
          </TextBase>
          <br />

          <li>
            <strong>Denúncia do contrato à distribuidora</strong>
          </li>
          <br />

          <TextBase>
            Ao decidir pela migração para o mercado livre, o próximo passo é
            enviar uma carta à distribuidora comunicando a denúncia dos
            contratos vigentes, sendo necessário o pagamento de multa nos casos
            em que o consumidor deseja antecipar o encerramento do contrato.
          </TextBase>
          <br />

          <li>
            <strong>Comprar energia no ACL</strong>
          </li>
          <br />
          <TextBase>
            O próximo passo é a negociação e aquisição de energia no mercado
            livre, por meio de contratos de compra de energia{' '}
            <strong>
              em ambiente de contratação livre (CCEAL) e/ou de contratos de
              compra de energia incentivada (CCEI)
            </strong>
            . O contrato pode ser firmado com comercializadores, geradores ou
            outros consumidores (por meio de cessão).
          </TextBase>
          <br />

          <li>
            <strong>Adequação ao sistema de medição</strong>
          </li>
          <br />
          <TextBase>
            Em seguida é necessário realizar a adequação do{' '}
            <strong>sistema de medição para faturamento (SMF)</strong> junto à
            distribuidora, conforme legislação vigente.
          </TextBase>
          <br />

          <li>
            <strong>Realizar adesão à CCEE e fazer a modelagem da carga</strong>
          </li>
          <br />
          <TextBase>
            O último passo para a migração do consumidor ao mercado livre de
            energia é a sua adesão à <strong>CCEE</strong>, bem como a modelagem
            do seu ativo de consumo.
          </TextBase>
        </ol>
        <br />
      </div>
    )
  },
  {
    title: 'Quais as vantagens e desvantagens do mercado livre de energia? ',
    content: (
      <div className={styles.content}>
        <TextBase className="text-center">
          O mercado livre de energia oferece vantagens e desvantagens, sendo
          elas:
        </TextBase>

        <br />
        <div className={styles.imageWrapper}>
          <MarketComparison
            firstColData={freeMarketAdvantages}
            secondColData={freeMarketDisadvantages}
            firstColHeadingBgColor="bg-orange"
            secondColHeadingBgColor="bg-v2-bleu-de-france"
          />
        </div>
        <br />
        <TextBase>
          Entretanto, todas as desvantagens do mercado livre de energia podem
          ser evitadas com a ajuda de uma consultoria especializada, como a do
          Portal Solar.
        </TextBase>
        <br />
        <TextBase>
          Nossa equipe está pronta para realizar a migração e gestão da sua
          empresa no mercado livre de energia para que você possa aproveitar
          todas as vantagens de forma otimizada e sem preocupações. Entre em
          contato e entenda o que é o mercado livre de energia e saiba{' '}
          <Link href="/como-economizar-energia" passHref target="blank">
            como economizar energia.
          </Link>
        </TextBase>
      </div>
    )
  },
  {
    title: 'Como comprar e vender energia no mercado livre de energia?',
    content: (
      <div className={styles.content}>
        <TextBase>
          <strong>No mercado livre</strong>, a compra e venda de energia é feita
          diretamente entre os agentes do ambiente, ou seja, entre consumidores,
          geradores e comercializadores, através de contratos bilaterais com as
          condições comerciais acordadas entre ambas as partes.
        </TextBase>
        <br />
        <TextBase>
          <strong>
            Os consumidores livres também podem vender energia no ACL para
            agentes geradores
          </strong>
          , comercializadores ou mesmo outros consumidores nos casos em que
          ocorram excedente entre a energia contratada e consumida.
        </TextBase>
        <br />
        <TextBase>
          Ou seja, caso um consumidor do <strong>mercado livre</strong>, em
          determinado mês, consuma menos energia do que a contratada, ele pode
          negociar livremente essa sobra para outros agentes. Caso contrário,
          esse excedente será pago pela <strong>CCEE</strong> com base no valor
          vigente do <strong> PLD. </strong>
        </TextBase>
      </div>
    )
  },
  {
    title: 'Como comprar energia solar no mercado livre de energia? ',
    content: (
      <div className={styles.content}>
        <TextBase>
          Os consumidores que desejam comprar{' '}
          <strong>energia solar no mercado livre</strong> devem procurar por
          ajuda especializada.
        </TextBase>
        <br />
        <TextBase>
          <strong>
            {' '}
            O Portal Solar é o primeiro e maior site de energia fotovoltaica do
            Brasil
          </strong>
          , e tem a solução ideal em venda de energia solar no{' '}
          <strong> mercado livre</strong> para você.
        </TextBase>
        <br />
        <TextBase>
          Entre em contato com a nossa equipe pelo e-mail{' '}
          <Link href="mailto:mercadolivre@portalsolar.com.br" target="_blank">
            mercadolivre@portalsolar.com.br
          </Link>
          , entenda o que é o mercado livre de energia,{' '}
          <Link
            href="/por-que-investir-em-energia-solar"
            passHref
            target="blank"
          >
            por que investir em energia solar
          </Link>{' '}
          e garanta a melhor solução para o seu negócio!
        </TextBase>
      </div>
    )
  },
  {
    title: 'Como entrar no mercado livre de energia? Quais os requisitos? ',
    content: (
      <div className={styles.content}>
        <TextBase>
          <strong>
            Para entrar no mercado livre de energia elétrica é preciso atender
            os seguintes requisitos:
          </strong>
        </TextBase>
        <br />
        <ul>
          <li>
            Demanda contratada mínima de 500kW ou atender ao requisito para
            migração em comunhão.
          </li>
        </ul>
        <br />
        <TextBase>
          <strong>
            Ficou com dúvidas do que é o mercado livre de energia? Entre em
            contato com a nossa equipe pelo e-mail{' '}
          </strong>
          <Link
            href="mailto:mercadolivre@portalsolar.com.br"
            passHref
            target="_blank"
          >
            mercadolivre@portalsolar.com.br
          </Link>
        </TextBase>
      </div>
    )
  },
  {
    title: 'Qual o preço da energia elétrica no mercado livre?',
    content: (
      <div className={styles.content}>
        <TextBase>
          Como podem ser negociados entre os consumidores e produtores, os
          preços da energia elétrica no mercado livre tendem a ser muito mais
          baratos que no mercado regulado, chegando a proporcionar uma economia
          de cerca de 29% na conta de luz.
        </TextBase>
        <br />
        <TextBase>
          Além disso, o preço da energia no <strong>ambiente livre</strong>{' '}
          tende a ser mais barato conforme a duração dos contratos de compra e
          venda, quanto maior o prazo, menor o preço da energia no{' '}
          <strong>ACL</strong>.
        </TextBase>
      </div>
    )
  },
  {
    title: 'O que é um autoprodutor de energia no mercado livre?',
    content: (
      <div className={styles.content}>
        <TextBase>
          Um autoprodutor de energia elétrica é todo consumidor do mercado
          livre, pessoa física, jurídica ou conjunto de empresas em consórcio ou
          em <strong>Sociedade de Propósito Específico (SPE)</strong>, que
          investe na geração da sua própria energia por meio da aquisição ou
          construção de usina(s), suprindo, total ou parcialmente, o seu
          consumo.
        </TextBase>
        <br />
        <TextBase>
          Para tanto, é obrigatório ser um agente da <strong>CCEE</strong> e
          obter outorga da <strong>ANEEL</strong>, este último apenas quando a
          potência da usina for maior que 5MW, caso contrário a usina
          necessitará apenas de um registro na ANEEL como empreendimentos de
          capacidade reduzida.
        </TextBase>
        <br />
        <TextBase>
          Os consumidores que optam pela autoprodução no <strong>ACL</strong>{' '}
          podem instalar a usina no próprio local de consumo (in situ),
          modalidade chamada de autoprodução contígua, ou em locais distintos,
          modalidade de autoprodução remota, com a diferença que, na segunda,
          será preciso pagar o custeio do uso da rede de
          transmissão/distribuição.
        </TextBase>
        <br />
        <TextBase>
          Nesse aspecto, a{' '}
          <Link href="/energia-solar" passHref target="_blank">
            energia solar{' '}
          </Link>
          se tornou, hoje, a melhor escolha para a autoprodução de energia no
          mercado livre, uma vez que a grande disponibilidade de luz solar no
          Brasil permite a construção de{' '}
          <Link href="/usina-solar.html" passHref target="_blank">
            usinas solares{' '}
          </Link>
          em praticamente qualquer região do país.
        </TextBase>
        <br />
        <TextBase>
          Existe ainda o modelo de autoprodução por equiparação, no qual
          consumidores com carga igual ou maior que 3MW, e que possuam
          participação societária em empresas detentoras de ativos de geração,
          podem ser “equiparados” a autoprodutores para fins de não incidência
          de encargos, nos termos da <strong>Lei Nº 11.488/2017</strong>.
        </TextBase>
      </div>
    )
  },
  {
    title:
      'Qual a diferença entre autoprodutor e produtor independente de energia?',
    content: (
      <div className={styles.content}>
        <TextBase>
          <strong>No mercado livre</strong>, o autoprodutor é aquele que produz
          energia para suprir o próprio consumo, enquanto o produtor
          independente é aquele que produz energia para comercializá-la.
        </TextBase>
        <br />
        <TextBase>
          Ou seja, o autoprodutor é o agente do ACL cadastrado na{' '}
          <strong>CCEE</strong> e autorizado pela <strong>ANEEL</strong> para
          produzir energia elétrica destinada ao seu uso exclusivo, e o produtor
          independente é o agente que recebe concessão ou autorização para
          comercializar toda ou parte da energia que produz no mercado de
          energia.
        </TextBase>
      </div>
    )
  },
  {
    title: 'O que é a abertura do mercado de energia livre? ',
    content: (
      <div className={styles.content}>
        <TextBase>
          Com a <strong>aprovação do PL 1917/15</strong>, criou-se a
          possibilidade da modernização do setor elétrico brasileiro com o ato
          de propor mudanças na legislação, visando a liberdade de escolha do
          consumidor. Este tema vem sendo discutido pelas Portarias nº 514/2018
          e 465/2019.
        </TextBase>
        <br />
        <TextBase>
          <strong>O Ministério de Minas e Energia - MME</strong>, por meio da
          Portaria 465/2019, na qual aborda as possibilidades da livre
          contratação de energia elétrica por parte dos consumidores, prevê em
          seu cronograma que a partir de janeiro de 2023 os consumidores com
          carga igual ou superior a 500 kW estarão livres para contratar
          qualquer tipo de energia, não só energia de fonte especial, como é
          determinado pela regulação atual.
        </TextBase>
        <br />
        <TextBase>
          A portaria citada é fruto do amadurecimento do tema da abertura do
          mercado de energia que tem como objetivo conferir maior eficiência e
          competitividade, além de estar alinhada aos padrões internacionais de
          liberdade de escolha dos consumidores.
        </TextBase>
        <br />
        <TextBase>
          Em janeiro deste ano, a{' '}
          <strong>
            ABRACEEL – Associação Brasileira dos Comercializadores de Energia
          </strong>{' '}
          apresentou ao MME, proposta de cronograma que prevê a abertura do
          mercado livre para todos os consumidores brasileiros a partir de 2026.
        </TextBase>
      </div>
    )
  },
  {
    title:
      'Qual a quantidade de energia consumida no mercado livre de energia?',
    content: (
      <div className={styles.content}>
        <TextBase>
          Com o aumento do número de consumidores, também segue crescendo o
          consumo de energia no ambiente de contratação livre.
        </TextBase>
        <br />
        <TextBase>
          Segundo os dados da CCEE, o ACL respondeu por 35,73% de toda a energia
          consumida no Brasil em abril de 2022.
        </TextBase>
        <br />
        <TextBase className="text-center">
          <strong>
            Veja, abaixo, o comparativo entre o consumo de energia no mercado
            livre e no mercado cativo em cada submercado do país e a sua
            evolução no último ano:
          </strong>
        </TextBase>
        <br />
        <div className={styles.imageWrapper}>
          <figure>
            <img
              src="/assets/mercado-regulado-e-mercado-livre-de-energia.svg"
              alt="Mercado regulado e mercado livre de energia - comparação do consumo"
            />
            <figcaption>
              Fonte: Infomercado mensal CCEE, contabilização de abril de 2022.
            </figcaption>
          </figure>
        </div>
        <br />
        <div className={styles.imageWrapper}>
          <img
            src="/assets/compra-de-energia-no-mercado-livre.svg"
            alt="Compra de energia no mercado livre - consumo de energia por submercado"
          />
        </div>
      </div>
    )
  },
  {
    title: 'Como está o uso da energia solar no mercado livre de energia? ',
    content: (
      <div className={styles.content}>
        <TextBase>
          Nos últimos anos, a fonte solar fotovoltaica tornou-se uma das opções
          mais baratas de geração de energia elétrica, graças ao avanço
          tecnológico, mudanças de regulamentação e à velocidade de
          implementação das usinas.
        </TextBase>
        <br />
        <TextBase>
          Dessa forma, a geração solar tornou-se uma grande tendência no mercado
          livre de energia, com grandes projetos sendo desenvolvidos
          especificamente para atender à demanda de{' '}
          <strong>consumidores livres.</strong>
        </TextBase>
        <br />
        <TextBase>
          Uma nova tendência é a figura do autoprodutor de energia, situação em
          que o consumidor opta por investir na geração de sua própria energia,
          a partir da aquisição ou construção de empreendimento de geração, para
          atendimento parcial o total de seu consumo (uso exclusivo).
        </TextBase>
        <br />
        <TextBase>
          Entre as{' '}
          <Link href="/energia-solar-vantagens" passHref target="_blank">
            vantagens da energia solar{' '}
          </Link>
          no mercado livre, é que ela também possibilita a obtenção dos
          certificados de energia renovável (I-REC), indispensáveis para as
          empresas que estão alinhadas aos princípios ESG e antenadas à essa
          tendência mundial.
        </TextBase>
      </div>
    )
  },
  {
    title: 'O que é mercado cativo de energia? ',
    content: (
      <div className={styles.content}>
        <TextBase>
          A forma tradicional de consumir energia no Brasil acontece no Ambiente
          de Contratação Regulada (ACR), também conhecido como mercado cativo.
          Nesse cenário, o cliente é obrigado a consumir energia elétrica da
          concessionária de distribuição de sua região e pagar as tarifas
          mensais, sem possibilidade de negociar condições.
        </TextBase>
        <br />
        <TextBase>
          Atualmente, todos os consumidores residenciais do Brasil estão
          incluídos nessa categoria. Dessa forma, ainda não existe um mercado
          livre de energia residencial. Porém, já são debatidas mudanças de
          regulação para permitir a abertura total do mercado de energia.
        </TextBase>
      </div>
    )
  }
]
